<script setup lang="ts">
import {
  MessageProcessor,
  NodeResolver,
  effect,
  getValue,
  type A2uiClientAction,
  type ComponentNode,
} from '@a2ui/web_core/v0_9'
import {
  A2UI_CATALOG,
  toA2uiMessages,
  type A2uiSurfaceInput,
} from '~/utils/a2ui'

const { surfaceId, surface } = defineProps<{
  surfaceId: string
  surface: A2uiSurfaceInput
}>()

const emit = defineEmits<{ action: [action: A2uiClientAction] }>()

const root = shallowRef<ComponentNode>()
const error = ref<string>()

/* Keyed on the serialized payload: the transcript is re-broadcast after every
 * turn with fresh objects, which must not reset what the user typed. The parse
 * also gives the renderer its own copy to write input into. */
const payload = computed(() => JSON.stringify(surface))

watch(
  [payload, () => surfaceId],
  ([json, id], _, onCleanup) => {
    error.value = undefined
    const processor = new MessageProcessor([A2UI_CATALOG], (action) =>
      emit('action', action),
    )
    onCleanup(() => processor.model.dispose())

    try {
      processor.processMessages(toA2uiMessages(id, JSON.parse(json)))
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : String(cause)
      return
    }

    const model = processor.model.getSurface(id)!
    const onError = model.onError.subscribe((event: { message?: string }) => {
      error.value = event.message ?? 'Invalid component'
    })
    const resolver = new NodeResolver(model, A2UI_CATALOG)
    const stop = effect(() => {
      root.value = getValue(resolver.rootNode)
    })

    onCleanup(() => {
      stop()
      resolver.dispose()
      onError.unsubscribe()
    })
  },
  { immediate: true },
)
</script>

<template>
  <UAlert
    v-if="error"
    :description="error"
    color="warning"
    icon="i-ph-warning"
    title="This response's UI couldn't be displayed"
    variant="subtle"
  />
  <div v-else-if="root" class="text-sm">
    <A2uiNode :node="root" />
  </div>
</template>
